import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface IDetailRow {
  label: string;
  value: React.ReactNode;
}

interface ReviewCampaignModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  subtitle: string;
  campaignName: string;
  templateName: string;
  details?: IDetailRow[];
  isSubmitting?: boolean;
}

const ReviewCampaignModal: React.FC<ReviewCampaignModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  campaignName,
  templateName,
  details = [],
  isSubmitting = false,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Campaign & Template summary card */}
        <Box
          sx={{
            bgcolor: "grey.100",
            borderRadius: 1,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            ZNS Campaign
          </Typography>
          <Typography variant="body1" fontWeight={700} mb={1.5}>
            {campaignName || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ZNS Template
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {templateName || "—"}
          </Typography>
        </Box>

        {/* Optional detail rows */}
        {details.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {details.map((row) => (
              <Box
                key={row.label}
                sx={{ display: "flex", alignItems: "baseline", gap: 2 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 160 }}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ whiteSpace: "pre-line" }}
                >
                  {row.value ?? "—"}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            BACK
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "CONFIRMING..." : "CONFIRM"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewCampaignModal;
